import React, { useState, useCallback, useRef } from 'react';
import { 
  PhotoIcon, 
  SparklesIcon, 
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CloudArrowUpIcon,
  TrophyIcon,
  TagIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  LightBulbIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { AnalysisResult, ApiProvider } from '../../types';
import { ProductCategory } from '../../config/analysisPrompts';
import { uploadImageDirectly } from '../../services/apiService';
import { scanService } from '../../services/scanService';
import { statsEmitter } from '../../utils/statsEmitter';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { analyzeImageWithGemini } from '../../services/geminiService';
import GeminiAnalysisLoading from '../../components/GeminiAnalysisLoading';
import CategorySelector from '../../components/CategorySelector';
import MultiAngleCarousel from '../../components/MultiAngleCarousel';
import ReactMarkdown from 'react-markdown';

interface Step {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
  icon: React.ComponentType<any>;
}

const AnalyzeGeminiPage: React.FC = () => {
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Core state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>(ProductCategory.AUTO_DETECT);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showImageComparison, setShowImageComparison] = useState(true);
  const [showNewAnalysisConfirm, setShowNewAnalysisConfirm] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<'upload' | 'analyze' | 'generate' | 'complete'>('upload');

  const steps: Step[] = [
    {
      id: 1,
      title: 'Upload',
      description: 'Select your image',
      completed: !!selectedImage,
      active: currentStep === 1,
      icon: CloudArrowUpIcon,
    },
    {
      id: 2,
      title: 'AI Analysis',
      description: 'Gemini analyzes your item',
      completed: !!analysisResult,
      active: currentStep === 2,
      icon: SparklesIcon,
    },
    {
      id: 3,
      title: 'Results',
      description: 'View analysis results',
      completed: !!analysisResult,
      active: currentStep === 3,
      icon: TrophyIcon,
    },
  ];

  const performAnalysis = useCallback(async (imageUrl: string) => {
    if (!selectedImage) return;

    try {
      setIsProcessing(true);
      setError(null);
      setCurrentStep(2);
      setAnalysisPhase('analyze');

      // Simulate realistic timing phases with proper progression
      const analysisTimer = setTimeout(() => setAnalysisPhase('generate'), 4000);
      const completeTimer = setTimeout(() => setAnalysisPhase('complete'), 10000);

      console.log('Starting Gemini analysis with URL:', imageUrl, 'and category:', selectedCategory);
      const result = await analyzeImageWithGemini(imageUrl, selectedCategory);
      console.log('Gemini analysis completed successfully:', result);
      
      // Clear any pending timers
      clearTimeout(analysisTimer);
      clearTimeout(completeTimer);
      
      // Ensure we're in complete phase before showing results
      setAnalysisPhase('complete');
      setTimeout(async () => {
        setAnalysisResult(result);
        setCurrentStep(3);
        setIsProcessing(false);
        showToast('success', 'Analysis Complete!', 'Your item has been analyzed successfully.');

        // Automatically save to database if user is authenticated
        if (isAuthenticated) {
          try {
            await scanService.createScan({
              title: result.title,
              description: result.description,
              value: result.value,
              aiExplanation: result.aiExplanation,
              imageUrl: imageUrl,
              apiProvider: ApiProvider.GEMINI,
              visualMatches: result.visualMatches || [],
            });
            statsEmitter.emit();
          } catch (saveError) {
            console.error('Failed to auto-save scan:', saveError);
            // Don't show error to user as analysis was successful
          }
        }
      }, 1500);
    } catch (error) {
      console.error('Analysis failed:', error);
      setError(error instanceof Error ? error.message : 'Analysis failed');
      showToast('error', 'Analysis Failed', error instanceof Error ? error.message : 'Please try again.');
      setIsProcessing(false);
    }
  }, [selectedImage, selectedCategory, showToast]);

  const handleImageSelected = useCallback(async (file: File) => {
    try {
      setSelectedImage(file);
      setAnalysisResult(null);
      setError(null);
      setCurrentStep(0);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      showToast('success', 'Image Selected', 'Ready for analysis! Choose a category and click Analyze.');
    } catch (error) {
      console.error('Image selection failed:', error);
      setError(error instanceof Error ? error.message : 'Image selection failed');
      showToast('error', 'Selection Failed', 'Please try again.');
    }
  }, [showToast]);

  const handleAnalyzeClick = useCallback(async () => {
    if (!selectedImage) return;

    try {
      setCurrentStep(1);
      setIsProcessing(true);
      setError(null);
      setAnalysisPhase('upload');

      console.log('Starting upload for file:', selectedImage.name);
      const uploadResult = await uploadImageDirectly(selectedImage);
      console.log('Upload completed, result:', uploadResult);
      
      if (typeof uploadResult === 'string') {
        console.log('Valid URL received, starting analysis with URL:', uploadResult);
        await performAnalysis(uploadResult);
      } else {
        console.error('Invalid upload result:', uploadResult);
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setError(error instanceof Error ? error.message : 'Upload failed');
      showToast('error', 'Upload Failed', 'Please try again.');
      setIsProcessing(false);
      setCurrentStep(0);
    }
  }, [selectedImage, performAnalysis, showToast]);

  const clearImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResult(null);
    setError(null);
    setCurrentStep(0);
    setShowFullDescription(false);
    setShowImageComparison(true);
    setShowNewAnalysisConfirm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelected(file);
    }
  }, [handleImageSelected]);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSaveAnalysis = useCallback(async () => {
    if (!analysisResult) return;

    if (!isAuthenticated) {
      showToast('error', 'Please sign in to save analyses', 'You need to be logged in to save your analysis results.');
      return;
    }

    try {
      await scanService.createScan({
        title: analysisResult.title,
        description: analysisResult.description,
        value: analysisResult.value,
        aiExplanation: analysisResult.aiExplanation,
        imageUrl: analysisResult.imageUrl || imagePreview || undefined,
        apiProvider: ApiProvider.GEMINI,
        visualMatches: analysisResult.visualMatches || [],
      });
      statsEmitter.emit();
      showToast('success', 'Analysis Saved!', 'Your analysis has been saved to your history.');
    } catch (error) {
      console.error('Failed to save analysis:', error);
      showToast('error', 'Save Failed', 'Could not save analysis. Please try again.');
    }
  }, [analysisResult, imagePreview, showToast, isAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-full">
              <SparklesIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            AI-Powered Item Analysis
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Upload an image and let our AI analyze your item with Gemini's advanced computer vision
          </p>
        </div>

        {/* Progress Steps - Only show when not processing */}
        {!isProcessing && (
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                      step.completed
                        ? 'bg-green-900 border-green-400 text-green-300'
                        : step.active
                        ? 'bg-blue-900 border-blue-400 text-blue-300'
                        : 'bg-gray-800 border-gray-600 text-gray-400'
                    }`}>
                      {step.completed ? (
                        <CheckCircleIcon className="h-6 w-6" />
                      ) : (
                        <Icon className="h-6 w-6" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        step.completed ? 'text-green-300' : step.active ? 'text-blue-300' : 'text-gray-400'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`ml-8 w-16 h-0.5 ${
                        steps[index + 1].completed ? 'bg-green-400' : 'bg-gray-600'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content Area */}
        {isProcessing ? (
          /* Loading Screen replaces everything during analysis */
          <div className="mb-8">
            <GeminiAnalysisLoading currentPhase={analysisPhase} />
          </div>
        ) : (
          /* Upload Section - Only show when not processing */
          <div className="bg-gray-800 rounded-2xl shadow-lg p-8 mb-8 border border-gray-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Image Upload */}
              <div>
                <label className="block text-lg font-semibold text-white mb-4">
                  <PhotoIcon className="h-6 w-6 inline mr-2" />
                  Upload Your Item Image
                </label>
                
                {!imagePreview ? (
                  <div 
                    onClick={triggerFileInput}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-12 text-center hover:border-blue-500 hover:bg-gray-700 transition-colors duration-200 cursor-pointer"
                  >
                    <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-xl font-medium text-gray-200 mb-2">Click to upload an image</p>
                    <p className="text-gray-400">PNG, JPG, GIF up to 10MB</p>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-64 object-contain bg-gray-700 rounded-xl border border-gray-600"
                    />
                    <button
                      onClick={clearImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                    <div className="mt-4 p-4 bg-green-900 rounded-lg border border-green-700">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                        <span className="text-green-300 font-medium">Image ready for analysis</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>

              {/* Category Selection & Analysis */}
              <div>
                <label className="block text-lg font-semibold text-white mb-4">
                  <TagIcon className="h-6 w-6 inline mr-2" />
                  Select Category
                </label>
                
                <CategorySelector
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  className="mb-6"
                />

                <button
                  onClick={handleAnalyzeClick}
                  disabled={!selectedImage}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    !selectedImage
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <SparklesIcon className="h-6 w-6 mr-2" />
                    Analyze with AI
                  </div>
                </button>

                {error && (
                  <div className="mt-4 p-4 bg-red-900 rounded-lg border border-red-700">
                    <div className="flex items-center">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                      <span className="text-red-300">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {analysisResult && !isProcessing && (
          <div className="bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-700">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TrophyIcon className="h-8 w-8 text-white mr-3" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Analysis Complete</h2>
                    <p className="text-green-100">Your item has been thoroughly analyzed</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-green-100 text-sm">Provider</p>
                  <p className="text-white font-semibold">{analysisResult.apiProvider}</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* AI Generated Multi-Angle Comparison */}
              {(analysisResult.comparisonImages || analysisResult.imageCompare) && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white flex items-center">
                      <EyeIcon className="h-6 w-6 mr-2 text-purple-400" />
                      AI Generated Reference Images
                    </h3>
                    <button
                      onClick={() => setShowImageComparison(!showImageComparison)}
                      className="flex items-center text-purple-400 hover:text-purple-300 font-medium"
                    >
                      {showImageComparison ? (
                        <>
                          <ChevronUpIcon className="h-5 w-5 mr-1" />
                          Hide
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="h-5 w-5 mr-1" />
                          Show
                        </>
                      )}
                    </button>
                  </div>
                  
                  {showImageComparison && (
                    <>
                      {analysisResult.comparisonImages ? (
                        // Multi-angle carousel view
                        <MultiAngleCarousel
                          images={analysisResult.comparisonImages}
                          userImage={imagePreview || ''}
                        />
                      ) : analysisResult.imageCompare ? (
                        // Fallback to single image view
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-purple-900/30 rounded-xl border border-purple-700">
                          <div>
                            <h4 className="font-medium text-white mb-3">Your Image</h4>
                            <img 
                              src={imagePreview || ''} 
                              alt="Your uploaded item" 
                              className="w-full h-48 object-contain bg-gray-700 rounded-lg border border-gray-600"
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-white mb-3">AI Reference</h4>
                            <img 
                              src={analysisResult.imageCompare} 
                              alt="AI generated reference" 
                              className="w-full h-48 object-contain bg-gray-700 rounded-lg border border-gray-600"
                            />
                            <p className="text-sm text-purple-400 mt-2">
                              Generated using Google's Imagen AI for comparison
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              )}

              {/* Main Analysis Content */}
              <div className="space-y-8">
                {/* Overview Section */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                    <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-400" />
                    Analysis Overview
                  </h3>
                  <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-700">
                    <div className="prose max-w-none text-gray-300">
                      <ReactMarkdown>
                        {showFullDescription ? analysisResult.description : 
                         analysisResult.description.length > 300 ? 
                         analysisResult.description.substring(0, 300) + '...' : 
                         analysisResult.description}
                      </ReactMarkdown>
                      {analysisResult.description.length > 300 && (
                        <button
                          onClick={() => setShowFullDescription(!showFullDescription)}
                          className="mt-3 text-blue-400 hover:text-blue-300 font-medium"
                        >
                          {showFullDescription ? 'Show Less' : 'Read More'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Value and Analysis */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-400" />
                      Estimated Value
                    </h3>
                    <div className="bg-green-900/30 rounded-xl p-4 border border-green-700">
                      <p className="text-2xl font-bold text-green-300">{analysisResult.value}</p>
                      <p className="text-green-400 text-sm mt-1">Market estimate</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <CheckCircleIcon className="h-5 w-5 mr-2 text-blue-400" />
                      AI Analysis
                    </h3>
                    <div className="bg-blue-900/30 rounded-xl p-4 border border-blue-700">
                      <p className="text-xl font-semibold text-blue-300">{analysisResult.title}</p>
                      <p className="text-blue-400 text-sm mt-1">Item identification</p>
                    </div>
                  </div>
                </div>

                {/* AI Explanation */}
                {analysisResult.aiExplanation && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <LightBulbIcon className="h-5 w-5 mr-2 text-amber-400" />
                      AI Explanation
                    </h3>
                    <div className="bg-amber-900/30 rounded-xl p-6 border border-amber-700">
                      <div className="prose max-w-none text-gray-300">
                        <ReactMarkdown>
                          {analysisResult.aiExplanation}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save Analysis Button */}
                <div className="flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    onClick={handleSaveAnalysis}
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 space-x-3"
                  >
                    <DocumentTextIcon className="h-6 w-6" />
                    <span>Save Analysis</span>
                  </button>
                  <button
                    onClick={() => setShowNewAnalysisConfirm(true)}
                    className="inline-flex items-center justify-center px-8 py-4 border border-blue-500 text-blue-300 hover:bg-blue-500/10 font-semibold rounded-xl transition-all duration-200 space-x-3"
                  >
                    <ArrowPathIcon className="h-6 w-6" />
                    <span>Analyze Another</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showNewAnalysisConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-gemini-analysis-title"
          >
            <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
              <h2 id="new-gemini-analysis-title" className="text-xl font-bold text-white">
                Analyze another item?
              </h2>
              <p className="mt-2 text-gray-300">
                This will clear the current item so you can upload a new one. Your completed result remains in your history.
              </p>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowNewAnalysisConfirm(false)}
                  className="rounded-lg px-4 py-2 font-medium text-gray-300 transition-colors hover:bg-gray-700"
                >
                  Keep viewing results
                </button>
                <button
                  onClick={clearImage}
                  className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Start new analysis
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyzeGeminiPage;
